import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { GalleryHorizontal, Trash2, Upload } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { FileObject } from "@supabase/storage-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const AdminGallery = () => {
  console.log("[AdminGallery] Rendering AdminGallery component");
  const [images, setImages] = useState<FileObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<FileObject | null>(null);
  const { toast } = useToast();

  const fetchImages = async () => {
    try {
      const { data: files, error } = await supabase
        .storage
        .from('article-images')
        .list();

      if (error) {
        throw error;
      }

      console.log("[AdminGallery] Fetched images:", files);
      setImages(files);
    } catch (error) {
      console.error("[AdminGallery] Error fetching images:", error);
      toast({
        title: "Error",
        description: "Failed to load images",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, [toast]);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = fileName;

      const { error: uploadError } = await supabase.storage
        .from('article-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type
        });

      if (uploadError) throw uploadError;

      toast({
        title: "Success",
        description: "Image uploaded successfully",
      });

      await fetchImages();
    } catch (error) {
      console.error("[AdminGallery] Error uploading image:", error);
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const getImageUrl = (fileName: string) => {
    const { data } = supabase.storage
      .from('article-images')
      .getPublicUrl(fileName);
    return data.publicUrl;
  };

  const handleDeleteClick = (image: FileObject) => {
    setSelectedImage(image);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedImage) return;

    try {
      const { error } = await supabase
        .storage
        .from('article-images')
        .remove([selectedImage.name]);

      if (error) {
        throw error;
      }

      toast({
        title: "Success",
        description: "Image deleted successfully",
      });

      await fetchImages();
    } catch (error) {
      console.error("[AdminGallery] Error deleting image:", error);
      toast({
        title: "Error",
        description: "Failed to delete image",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedImage(null);
    }
  };

  return (
    <div className="min-h-screen py-24">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <GalleryHorizontal className="w-8 h-8" />
            <h1 className="text-3xl font-bold">Image Gallery</h1>
          </div>
          <div className="flex items-center gap-4">
            <Input
              type="file"
              accept="image/*"
              className="hidden"
              id="image-upload"
              onChange={handleUpload}
              disabled={uploading}
            />
            <Button
              onClick={() => document.getElementById('image-upload')?.click()}
              disabled={uploading}
            >
              <Upload className="mr-2 h-4 w-4" />
              {uploading ? "Uploading..." : "Upload Image"}
            </Button>
          </div>
        </div>
        
        {loading ? (
          <p>Loading images...</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image) => (
              <div 
                key={image.id} 
                className="relative group aspect-square overflow-hidden rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
              >
                <img
                  src={getImageUrl(image.name)}
                  alt={image.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="self-end bg-white/10 text-white border-white hover:bg-white/20"
                    onClick={() => handleDeleteClick(image)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <p className="text-white text-sm truncate w-full">
                    {image.name}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Image</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this image? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGallery;
